import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Password validation
  const validations = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    match: password === confirmPassword && password.length > 0,
  };

  const isValid = Object.values(validations).every(Boolean);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password/${token}?new_password=${encodeURIComponent(password)}`);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (error && !success) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">N</span>
              </div>
              <span className="text-2xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
                NYRAV TOURS
              </span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm text-center" data-testid="reset-password-error">
            <div className="w-16 h-16 bg-[#E76F51]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-[#E76F51]" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Link Expired
            </h1>
            <p className="text-[#4A4A4A] mb-6">
              {error}
            </p>
            <Link to="/admin/forgot-password">
              <Button className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full h-12">
                Request New Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">N</span>
              </div>
              <span className="text-2xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
                NYRAV TOURS
              </span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm text-center" data-testid="reset-password-success">
            <div className="w-16 h-16 bg-[#2A9D8F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#2A9D8F]" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Password Reset!
            </h1>
            <p className="text-[#4A4A4A] mb-6">
              Your password has been reset successfully. You can now login with your new password.
            </p>
            <Link to="/admin/login">
              <Button className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full h-12">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <span className="text-2xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
              NYRAV TOURS
            </span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm" data-testid="reset-password-form">
          <div className="w-16 h-16 bg-[#004E64]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[#004E64]" />
          </div>
          <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Create New Password
          </h1>
          <p className="text-[#4A4A4A] text-center mb-8">
            Your new password must be different from previous passwords.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm text-[#4A4A4A] mb-2 block">New Password</label>
              <div className="relative">
                <Input 
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl pr-12"
                  data-testid="reset-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4A4A4A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm text-[#4A4A4A] mb-2 block">Confirm Password</label>
              <div className="relative">
                <Input 
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 rounded-xl pr-12"
                  data-testid="reset-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4A4A4A]"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="bg-[#FDFBF7] rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-[#4A4A4A] mb-2">Password Requirements:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className={`flex items-center gap-2 ${validations.length ? 'text-[#2A9D8F]' : 'text-[#4A4A4A]'}`}>
                  {validations.length ? <CheckCircle className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                  8+ characters
                </div>
                <div className={`flex items-center gap-2 ${validations.uppercase ? 'text-[#2A9D8F]' : 'text-[#4A4A4A]'}`}>
                  {validations.uppercase ? <CheckCircle className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                  Uppercase letter
                </div>
                <div className={`flex items-center gap-2 ${validations.lowercase ? 'text-[#2A9D8F]' : 'text-[#4A4A4A]'}`}>
                  {validations.lowercase ? <CheckCircle className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                  Lowercase letter
                </div>
                <div className={`flex items-center gap-2 ${validations.number ? 'text-[#2A9D8F]' : 'text-[#4A4A4A]'}`}>
                  {validations.number ? <CheckCircle className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                  Number
                </div>
              </div>
              <div className={`flex items-center gap-2 text-sm ${validations.match ? 'text-[#2A9D8F]' : 'text-[#4A4A4A]'}`}>
                {validations.match ? <CheckCircle className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                Passwords match
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full h-12"
              disabled={loading || !isValid}
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
